import { ScheduleException } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/exception.pb';
import { Schedule } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule.pb';
import { ScheduleDefinition } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule_definitions.pb';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { RRule } from 'rrule';
import {
  AvailabilityExceptionInfo,
  CalendarStripeLineInfo,
  WorkingHourInfo,
} from '../../components/calendar-view/types';

dayjs.extend(utc);

export type CalendarEntitySchedule = Partial<Schedule> & {
  id: string;
  locationId: string;
  hasAnyWorkingHoursSet?: boolean;
  resolvedWorkingHours?: WorkingHourInfo[];
  resolvedExceptions?: AvailabilityExceptionInfo[];
};

type ScheduleDefinitionGroup = {
  locationId?: string;
  entityId: string;
  scheduleDefinitions?: ScheduleDefinition[];
};

type ScheduleExceptionEntityType = 'PROVIDER' | 'OPERATORY';

const DISPLAY_TIME_FORMAT = 'hh:mm A';

const getDateTime = (date: string, time = '00:00') => dayjs.utc(`${date}T${time.length === 5 ? `${time}:00` : time}`);

const getTimeDateTime = (time: string) => dayjs.utc(`2000-01-01T${time.length === 5 ? `${time}:00` : time}`);

const getDurationFromTimeRange = (startTime?: string, endTime?: string) => {
  if (!startTime || !endTime) {
    return 0;
  }

  const start = getTimeDateTime(startTime);
  const end = getTimeDateTime(endTime);
  return Math.max(end.diff(start, 'minute'), 0);
};

export const doesScheduleDefinitionApplyOnDate = (scheduleDefinition: ScheduleDefinition, calendarDate: string) => {
  if (!scheduleDefinition.startDate) {
    return false;
  }

  if (dayjs(calendarDate).isBefore(scheduleDefinition.startDate, 'day')) {
    return false;
  }

  if (scheduleDefinition.endDate && dayjs(calendarDate).isAfter(scheduleDefinition.endDate, 'day')) {
    return false;
  }

  if (!scheduleDefinition.rRule?.trim()) {
    return dayjs(calendarDate).isSame(scheduleDefinition.startDate, 'day');
  }

  try {
    const rule = new RRule({
      ...RRule.parseString(scheduleDefinition.rRule),
      dtstart: getDateTime(scheduleDefinition.startDate, scheduleDefinition.startTime).toDate(),
      ...(scheduleDefinition.endDate
        ? { until: getDateTime(scheduleDefinition.endDate, scheduleDefinition.endTime || '23:59').toDate() }
        : {}),
    });

    const startOfDay = dayjs(calendarDate).startOf('day').toDate();
    const endOfDay = dayjs(calendarDate).endOf('day').toDate();
    return rule.between(startOfDay, endOfDay, true).length > 0;
  } catch (e) {
    return dayjs(calendarDate).isSame(scheduleDefinition.startDate, 'day');
  }
};

export const toWorkingHourInfo = (scheduleDefinition: ScheduleDefinition): WorkingHourInfo | null => {
  const duration = getDurationFromTimeRange(scheduleDefinition.startTime, scheduleDefinition.endTime);
  if (!scheduleDefinition.startTime || duration <= 0) {
    return null;
  }

  return {
    startTime: getTimeDateTime(scheduleDefinition.startTime).format(DISPLAY_TIME_FORMAT),
    duration,
    breaks: [],
  };
};

export const hasCalendarScheduleWorkingHoursSet = (schedule?: CalendarEntitySchedule) => {
  if (!schedule) {
    return false;
  }

  if (typeof schedule.hasAnyWorkingHoursSet === 'boolean') {
    return schedule.hasAnyWorkingHoursSet;
  }

  return !!schedule.recurrenceRules?.workingHours?.length;
};

const getScheduleExceptionBreakInfo = (scheduleException: ScheduleException): CalendarStripeLineInfo | null => {
  if (!scheduleException.time?.startTime || !scheduleException.time?.endTime) {
    // all day exception
    return {
      startTime: '12:00 AM',
      duration: 24 * 60,
    };
  }
  const duration = getDurationFromTimeRange(scheduleException.time?.startTime, scheduleException.time?.endTime);
  if (!scheduleException.time?.startTime || duration <= 0) {
    return null;
  }

  return {
    startTime: getTimeDateTime(scheduleException.time.startTime).format(DISPLAY_TIME_FORMAT),
    duration,
  };
};

const getScheduleExceptionStartDate = (scheduleException: ScheduleException) => {
  if (!scheduleException.nextOccurrenceStart) {
    return '';
  }

  return dayjs(scheduleException.nextOccurrenceStart).format('YYYY-MM-DD');
};

const doesScheduleExceptionApplyOnDate = (scheduleException: ScheduleException, calendarDate: string) => {
  if (!scheduleException.nextOccurrenceStart) {
    return false;
  }

  const startDate = getScheduleExceptionStartDate(scheduleException);
  if (!startDate) {
    return false;
  }

  if (dayjs(calendarDate).isBefore(startDate, 'day')) {
    return false;
  }

  if (scheduleException.endDate && dayjs(calendarDate).isAfter(scheduleException.endDate, 'day')) {
    return false;
  }

  if (!scheduleException.recurrenceRule?.trim()) {
    return dayjs(calendarDate).isSame(startDate, 'day');
  }

  try {
    const rule = new RRule({
      ...RRule.parseString(scheduleException.recurrenceRule),
      dtstart: getDateTime(startDate, scheduleException.time?.startTime || '00:00').toDate(),
      ...(scheduleException.endDate
        ? { until: getDateTime(scheduleException.endDate, scheduleException.time?.endTime || '23:59').toDate() }
        : {}),
    });

    const startOfDay = dayjs(calendarDate).startOf('day').toDate();
    const endOfDay = dayjs(calendarDate).endOf('day').toDate();
    return rule.between(startOfDay, endOfDay, true).length > 0;
  } catch (e) {
    return dayjs(calendarDate).isSame(startDate, 'day');
  }
};

const doesScheduleExceptionMatchEntity = (
  scheduleException: ScheduleException,
  entityId: string,
  entityType: ScheduleExceptionEntityType
) => {
  if (scheduleException.target?.scopeType === 'LOCATION') {
    return true;
  }

  if (scheduleException.target?.scopeType !== 'RESOURCE') {
    return false;
  }

  if (scheduleException.target.logicOperator !== 'ANY') {
    return false;
  }

  return (scheduleException.target?.resources ?? []).some(
    (resource) => resource.type === entityType && resource.id === entityId
  );
};

const getEntityAvailabilityExceptionsFromScheduleExceptions = ({
  scheduleExceptions,
  entityId,
  entityType,
  calendarDate,
}: {
  scheduleExceptions: ScheduleException[];
  entityId: string;
  entityType: ScheduleExceptionEntityType;
  calendarDate: string;
}): CalendarStripeLineInfo[] => {
  return scheduleExceptions
    .filter((scheduleException) => doesScheduleExceptionMatchEntity(scheduleException, entityId, entityType))
    .filter((scheduleException) => doesScheduleExceptionApplyOnDate(scheduleException, calendarDate))
    .map(getScheduleExceptionBreakInfo)
    .filter((breakInfo): breakInfo is CalendarStripeLineInfo => breakInfo !== null)
    .sort((left, right) => left.startTime.localeCompare(right.startTime));
};

export const transformScheduleDefinitionGroupsToSchedules = (
  groups: ScheduleDefinitionGroup[],
  calendarDate: string,
  options?: {
    scheduleExceptions?: ScheduleException[];
    entityType?: ScheduleExceptionEntityType;
  }
): CalendarEntitySchedule[] => {
  return groups.map<CalendarEntitySchedule>(({ entityId, locationId = '', scheduleDefinitions = [] }) => {
    const filteredWorkingHours = scheduleDefinitions
      .filter((scheduleDefinition) => doesScheduleDefinitionApplyOnDate(scheduleDefinition, calendarDate))
      .map(toWorkingHourInfo)
      .filter((workingHour): workingHour is WorkingHourInfo => workingHour !== null)
      .sort((left, right) => left.startTime.localeCompare(right.startTime));

    const availabilityExceptions =
      options?.scheduleExceptions && options.entityType
        ? getEntityAvailabilityExceptionsFromScheduleExceptions({
            scheduleExceptions: options.scheduleExceptions,
            entityId,
            entityType: options.entityType,
            calendarDate,
          })
        : [];

    return {
      id: entityId,
      locationId,
      hasAnyWorkingHoursSet: scheduleDefinitions.some((sd) => sd.bookingStrategy === 'REQUEST'),
      resolvedWorkingHours: filteredWorkingHours,
      resolvedExceptions: availabilityExceptions,
    };
  });
};
